"use client";

import { useContext, useEffect, useRef, useState } from "react";
import { PageSection } from "@/components/common/page-section/page-section";
import { CardSlider } from "@/components/common/card-slider";
import { ImageHoverWrapper } from "@/components/common/image-hover-wrapper";
import { PictureUpload } from "@/components/common/picture-upload/picture-upload";
import { SuperLink as Link } from "@/components/common/super-link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  createPlaylistEpisodePath,
  createPlaylistPath,
  videoCardBadgeStyles,
} from "@/components/video/video-card/service";
import { ResourceContext } from "@/contexts/resource-context";
import { cn } from "@/lib/utils";
import { clientPost } from "@/service/client-api/client-api";
import { verboseDatetimeFormat } from "@/service/date/article";
import { trans } from "@/service/i18n/i18n";
import { logEvent } from "@/service/logs/log";
import { createPath } from "@/service/routing/routing";

type Card = {
  path: string;
  label: string;
  meta?: string;
  // ikona kategorii (svg) dla treści bez obrazka: poradniki, szybkie pytania, kalkulatory
  icon?: string;
};

// Te same ścieżki co RecommendationContextServer, tylko po `kind` z odpowiedzi slotu.
function toCard({ kind, preview: p }: RecommendationSlotItemVM): Card | null {
  switch (kind) {
    case "publication":
    case "vademecum":
      return {
        path: createPath(
          kind === "vademecum" ? "vademecum.show" : "publication.show",
          { categorySlug: p.category?.slug, slug: p.slug },
        ),
        label: p.category?.name,
        meta: p.publishedAt ? verboseDatetimeFormat(p.publishedAt) : "",
      };
    case "clinical_case":
      return {
        path: createPath("clinical_case.show", {
          slug: p.slug,
          categorySlug: p.category?.slug,
        }),
        label: trans("recommendation.kind.clinical_case"),
        meta: p.category?.name,
      };
    case "tutorial":
    case "fast_question":
      return {
        path: createPath(`${kind}.show`, {
          category: p.category?.slug,
          tutorial: p.slug,
        }),
        label: trans(`recommendation.kind.${kind}`),
        meta: p.subtitle ?? p.category?.name,
        icon: p.category?.svgContent,
      };
    case "calculator":
      return {
        path: createPath("calculator.show", {
          categorySlug: p.category?.slug ?? "",
          slug: p.slug ?? "",
        }),
        label: trans("recommendation.kind.calculator"),
        meta: p.subtitle ?? p.category?.name,
        icon: p.category?.iconSvgContent,
      };
    case "video":
    case "playlist":
      if (p.viewModelType === "playlistEpisodePreview") {
        return {
          path: createPlaylistEpisodePath(p),
          label: trans("recommendation.kind.video"),
          meta: p.playlist?.title,
        };
      }
      return {
        path: createPlaylistPath(p),
        label: trans("recommendation.kind.video"),
        meta:
          p.description ??
          (p.publishedAt ? verboseDatetimeFormat(p.publishedAt) : ""),
      };
    case "course":
      return {
        path: createPath("course.show", { slug: p.slug, id: p.id }),
        label: trans("recommendation.kind.course"),
        meta: p.subtitle,
      };
    case "ebook":
      return {
        path: createPath("ebook.show", { slug: p.slug }),
        label: trans("recommendation.kind.ebook"),
        meta: p.shortDescription,
      };
    default:
      return null;
  }
}

function withRec(path: string, requestId: string) {
  const url = new URL(path, "https://remedium.local");
  url.searchParams.set("rec", requestId);
  return url.pathname + url.search + url.hash;
}

// rec_impression raz na kartę na requestId, także po ponownym zamontowaniu slidera
const impressionsSent = new Set<string>();

const HomeForYouCard = ({
  item,
  card,
  slot,
}: {
  item: RecommendationSlotItemVM;
  card: Card;
  slot: RecommendationSlotVM;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  // bez `sponsored` strony głównej: zdarzenia slidera nie należą do patronatu banera hero
  const { logView: pageLogView } = useContext(ResourceContext);
  const logView = { ...pageLogView, sponsored: undefined };
  const href = withRec(card.path, slot.requestId);
  const tracking = {
    request_id: slot.requestId,
    slot_id: slot.slotId,
    item_id: `${item.kind}:${item.id}`,
    kind: item.kind,
    campaign_code: item.campaignCode,
    position: item.position,
    ring: item.ring,
    reason: item.reason,
    group: slot.group,
  };

  useEffect(() => {
    const key = `${slot.requestId}:${item.position}`;
    const element = ref.current;
    if (!element || impressionsSent.has(key)) {
      return;
    }

    let timer: ReturnType<typeof setTimeout> | undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          clearTimeout(timer);
          timer = undefined;
          return;
        }
        timer ??= setTimeout(() => {
          impressionsSent.add(key);
          observer.disconnect();
          logEvent("rec_impression", logView, {
            ...tracking,
            score: item.score,
          });
        }, 1000);
      },
      { threshold: 0.5 },
    );
    observer.observe(element);

    return () => {
      observer.disconnect();
      clearTimeout(timer);
    };
  }, [slot.requestId, item.position]);

  const onClick = () => {
    logEvent("rec_click", logView, tracking);
    // kliknięta karta nie wraca w sliderze (serwer zapisuje ją jako odwiedzoną); nawigacja nie czeka
    clientPost("recommendation.click", { kind: item.kind, id: item.id }).catch(
      () => {},
    );
  };
  const title = item.preview.title ?? item.preview.name;

  return (
    <div ref={ref} className={"flex flex-col"}>
      <ImageHoverWrapper rounded={"rounded-xl"} className={"mb-4"}>
        <Link href={href} onClick={onClick} className={"relative block"}>
          {item.sponsored && (
            <Badge
              className={cn(
                videoCardBadgeStyles,
                "top-3 left-3 z-10 rounded-full bg-white text-[10px] font-semibold text-black",
              )}
            >
              {trans("recommendation.home_for_you.sponsored")}
            </Badge>
          )}
          {item.preview.image ? (
            <PictureUpload
              image={item.preview.image}
              className={"h-full object-cover"}
              aspectRatio={"1.91 / 1"}
              type={"card"}
            />
          ) : (
            <div
              className={
                "bg-remedium-blue/5 flex aspect-[1.91/1] items-center justify-center [&_svg]:h-10 [&_svg]:w-10"
              }
              dangerouslySetInnerHTML={{ __html: card.icon ?? "" }}
            />
          )}
        </Link>
      </ImageHoverWrapper>
      <Link
        href={href}
        onClick={onClick}
        className={
          "text-foreground text-remedium-blue-hover mb-1.5 line-clamp-3 text-base font-[550]"
        }
      >
        {title}
      </Link>
      <div className={"flex items-center gap-2 text-sm text-zinc-500"}>
        <span className={"text-remedium-blue font-semibold whitespace-nowrap"}>
          {card.label}
        </span>
        {card.meta && (
          <>
            <span>·</span>
            <span className={"line-clamp-1"}>{card.meta}</span>
          </>
        )}
      </div>
    </div>
  );
};

export const HomeForYouSlider = ({ slot }: { slot: RecommendationSlotVM }) => {
  const [isWhyOpen, setIsWhyOpen] = useState(false);

  const cards = slot.items.flatMap((item) => {
    const card = toCard(item);
    return card
      ? [
          <HomeForYouCard
            key={`${item.kind}:${item.id}`}
            item={item}
            card={card}
            slot={slot}
          />,
        ]
      : [];
  });

  if (cards.length === 0) {
    return null;
  }

  return (
    <PageSection
      heading={
        <>
          <h2>{trans("recommendation.home_for_you.title")}</h2>
          {slot.explanation?.headline && (
            <p className={"text-sm font-normal text-zinc-500"}>
              {slot.explanation.headline}
            </p>
          )}
        </>
      }
      headingAdditions={
        slot.explanation?.reasons?.length > 0 && (
          <Button
            variant={"inline"}
            size={"inline"}
            onClick={() => setIsWhyOpen(true)}
          >
            {trans("recommendation.home_for_you.why")}
          </Button>
        )
      }
    >
      <CardSlider items={cards} />
      <Dialog open={isWhyOpen} onOpenChange={setIsWhyOpen}>
        <DialogContent className={"w-full max-w-md"} innerClassName={"p-6"}>
          <DialogTitle className={"mb-4 text-xl font-semibold text-black"}>
            {trans("recommendation.home_for_you.why")}
          </DialogTitle>
          <ul className={"list-disc space-y-2 pl-5 text-sm text-zinc-700"}>
            {slot.explanation?.reasons?.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
          <DialogClose />
        </DialogContent>
      </Dialog>
    </PageSection>
  );
};
